import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDocumentVersions } from '../../components/OfficeWorkspace/useOfficeWorkspace';
import { useAuth } from '../../contexts/authContext';
import { responseDocumentClient } from '../../lib/connect/clients';
import { uploadAttachmentViaTesoro } from '../../lib/uploadAttachment';
import { rfiSectionClient, useRfiSections } from './rfiSectionHooks';
import { RfiPackageDialog, responseItemLabel } from './workspace/RfiPackageDialog';
const emptyDraft = ()=>({
        name: '',
        packageId: '',
        sectionId: '',
        versionId: '',
        requests: {}
    });
/** Session-scoped recovery includes completed uploads, so retrying attachment never uploads twice. */ export function RfiPackagePanel(props) {
    const { user } = useAuth();
    const storageKey = `rfi-package:${user?.id}:${props.rfiResponseProjectId}`;
    return /*#__PURE__*/ _jsx(PackageComposer, {
        ...props,
        storageKey: storageKey
    }, storageKey);
}
function PackageComposer({ rfiResponseProjectId, zouProjectId, storageKey, preferredFormat, submissionFormat }) {
    const { user } = useAuth();
    const [draft, setDraft] = useState(()=>{
        try {
            return {
                ...emptyDraft(),
                ...JSON.parse(sessionStorage.getItem(storageKey) || '{}')
            };
        } catch  {
            return emptyDraft();
        }
    });
    const current = useRef(draft);
    const persist = useCallback((patch)=>{
        current.current = {
            ...current.current,
            ...patch
        };
        sessionStorage.setItem(storageKey, JSON.stringify(current.current));
        setDraft(current.current);
    }, [
        storageKey
    ]);
    const requestId = (key)=>{
        const existing = current.current.requests[key];
        if (existing) return existing;
        const id = crypto.randomUUID();
        persist({
            requests: {
                ...current.current.requests,
                [key]: id
            }
        });
        return id;
    };
    const sections = useRfiSections(rfiResponseProjectId);
    const packages = useQuery({
        queryKey: [
            'rfi-packages',
            rfiResponseProjectId
        ],
        queryFn: async ()=>(await rfiSectionClient.listRfiPackages({
                rfiResponseProjectId
            })).packages
    });
    const history = useQuery({
        queryKey: [
            'rfi-package-history',
            rfiResponseProjectId,
            draft.sectionId
        ],
        enabled: !!draft.sectionId,
        queryFn: async ()=>(await rfiSectionClient.listRfiSectionDocumentHistory({
                rfiResponseProjectId,
                sectionId: draft.sectionId
            })).history
    });
    // WRT-16: an RFI with a response document packages that document (one item).
    const responseDoc = useQuery({
        queryKey: [
            'rfi-package-response-document',
            rfiResponseProjectId
        ],
        retry: false,
        staleTime: 60000,
        queryFn: async ()=>(await responseDocumentClient.getResponseWorkspace({
                container: {
                    kind: 'rfi',
                    rfiResponseProjectId
                },
                documentId: ''
            })).activeDocumentId
    });
    const responseDocumentId = responseDoc.data || '';
    const responseVersions = useDocumentVersions({
        kind: 'rfi',
        rfiResponseProjectId
    }, responseDocumentId || undefined);
    const versionOptions = (responseVersions.data?.pages ?? []).flatMap((p)=>p.versions).map((v)=>({
            id: v.id,
            versionNumber: v.versionNumber,
            label: v.label
        }));
    const versionNumberOf = (id)=>versionOptions.find((v)=>v.id === id)?.versionNumber;
    const [dialogOpen, setDialogOpen] = useState(false);
    const selected = packages.data?.find((p)=>p.id === draft.packageId);
    const versions = history.data?.versions ?? [];
    const [file, setFile] = useState();
    const [progress, setProgress] = useState(0);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const lock = useRef(false);
    const run = async (label, action)=>{
        if (lock.current) return;
        lock.current = true;
        setBusy(label);
        setError('');
        try {
            await action();
        } catch (err) {
            setError(`${err instanceof Error ? err.message : 'Package action failed'}. Your choices are retained. Retry the action.`);
        } finally{
            lock.current = false;
            setBusy('');
        }
    };
    useEffect(()=>{
        const openSource = ()=>{
            const id = window.location.hash.replace('#rfi-package-item-', '');
            const sourcePackage = packages.data?.find((p)=>p.items.some((item)=>item.id === id) || `#rfi-package-${p.id}` === window.location.hash);
            if (sourcePackage && current.current.packageId !== sourcePackage.id) persist({
                packageId: sourcePackage.id
            });
            requestAnimationFrame(()=>document.getElementById(`rfi-package-item-${id}`)?.scrollIntoView());
        };
        window.addEventListener('hashchange', openSource);
        openSource();
        return ()=>window.removeEventListener('hashchange', openSource);
    }, [
        packages.data,
        persist
    ]);
    const scope = {
        rfiResponseProjectId,
        packageId: draft.packageId
    };
    const disabled = !!busy;
    return /*#__PURE__*/ _jsxs("section", {
        className: "max-w-3xl space-y-3 rounded-md border border-border bg-card p-4",
        id: "rfi-packages",
        "data-testid": Testids.rfiPackage.panel,
        "aria-busy": disabled,
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "text-lg font-medium",
                children: "Proposed final packages"
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: "Pin saved section versions, generate their DOCX copies, or upload externally authored final files. Source documents, government templates, attachments, working documents and history remain in this workspace. Package presentation does not change source intelligence. This does not record a submission."
            }),
            /*#__PURE__*/ _jsxs("output", {
                "aria-live": "polite",
                "data-testid": Testids.rfiPackage.status,
                children: [
                    busy || (packages.isLoading ? 'Loading packages…' : ''),
                    busy === 'Uploading external final file…' && ` ${progress}%`
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.rfiPackage.error,
                children: error
            }),
            packages.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Packages could not be loaded."
            }),
            sections.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Sections could not be loaded."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                disabled: disabled,
                onClick: ()=>{
                    void packages.refetch();
                    void sections.refetch();
                    if (draft.sectionId) void history.refetch();
                },
                "data-testid": Testids.rfiPackage.refresh,
                children: "Refresh packages and versions"
            }),
            packages.data?.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                children: "No packages yet. Name and create a proposed package to begin."
            }),
            responseDocumentId ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiPackage.create,
                        disabled: disabled || packages.isLoading || packages.isError,
                        onClick: ()=>setDialogOpen(true),
                        children: "Create package"
                    }),
                    dialogOpen ? /*#__PURE__*/ _jsx(RfiPackageDialog, {
                        error: error || undefined,
                        isCreating: busy === 'Creating package…',
                        onCreate: ({ name, format, documentVersionId })=>void run('Creating package…', async ()=>{
                                const result = await rfiSectionClient.createRfiPackage({
                                    rfiResponseProjectId,
                                    name,
                                    exportFormat: format,
                                    documentVersionId,
                                    requestId: requestId(`create:${name}:${format}:${documentVersionId}`)
                                });
                                if (!result.package) throw new Error('No package returned');
                                persist({
                                    packageId: result.package.id
                                });
                                setDialogOpen(false);
                                await packages.refetch();
                            }),
                        onOpenChange: setDialogOpen,
                        open: dialogOpen,
                        preferredFormat: preferredFormat,
                        submissionFormat: submissionFormat,
                        versions: versionOptions
                    }) : null
                ]
            }) : null,
            responseDocumentId ? null : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "New package name",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "New package name",
                            "data-testid": Testids.rfiPackage.name,
                            value: draft.name,
                            maxLength: 512,
                            disabled: disabled,
                            onChange: (e)=>persist({
                                    name: e.target.value
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiPackage.create,
                        disabled: disabled || !draft.name.trim() || packages.isLoading || packages.isError,
                        onClick: ()=>void run('Creating package…', async ()=>{
                                const name = draft.name.trim();
                                const result = await rfiSectionClient.createRfiPackage({
                                    rfiResponseProjectId,
                                    name,
                                    requestId: requestId(`create:${name}`)
                                });
                                if (!result.package) throw new Error('No package returned');
                                persist({
                                    packageId: result.package.id,
                                    name: ''
                                });
                                await packages.refetch();
                            }),
                        children: "Create package"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Proposed package",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Proposed package",
                    "data-testid": Testids.rfiPackage.packageSelect,
                    disabled: disabled,
                    value: draft.packageId,
                    onChange: (e)=>persist({
                            packageId: e.target.value
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Choose a package"
                        }),
                        packages.data?.map((p)=>/*#__PURE__*/ _jsx("option", {
                                value: p.id,
                                children: p.name
                            }, p.id))
                    ]
                })
            }),
            selected && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    responseDocumentId ? null : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Section to pin",
                                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": "Section to pin",
                                    "data-testid": Testids.rfiPackage.sectionSelect,
                                    disabled: disabled,
                                    value: draft.sectionId,
                                    onChange: (e)=>persist({
                                            sectionId: e.target.value,
                                            versionId: ''
                                        }),
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Choose a section"
                                        }),
                                        sections.data?.map((s)=>/*#__PURE__*/ _jsx("option", {
                                                value: s.id,
                                                children: s.title
                                            }, s.id))
                                    ]
                                })
                            }),
                            draft.sectionId && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    history.isLoading && /*#__PURE__*/ _jsx("output", {
                                        children: "Loading saved versions…"
                                    }),
                                    history.isError && /*#__PURE__*/ _jsx("p", {
                                        role: "alert",
                                        children: "Version history unavailable. Refresh versions to retry; no latest version will be substituted."
                                    }),
                                    !history.isLoading && !history.isError && !versions.length && /*#__PURE__*/ _jsx(SpecraText, {
                                        children: "Open this section in the writing surface to create a saved document, then refresh versions."
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraFormField, {
                                        label: "Exact saved document version",
                                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                            "aria-label": "Exact saved document version",
                                            "data-testid": Testids.rfiPackage.versionSelect,
                                            value: draft.versionId,
                                            disabled: disabled || history.isLoading || history.isError,
                                            onChange: (e)=>persist({
                                                    versionId: e.target.value
                                                }),
                                            children: [
                                                /*#__PURE__*/ _jsx("option", {
                                                    value: "",
                                                    children: "Choose an exact version"
                                                }),
                                                versions.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                                        value: v.id,
                                                        children: [
                                                            "Version ",
                                                            v.versionNumber,
                                                            " · ",
                                                            v.id,
                                                            v.id === history.data?.currentVersionId ? ' · Current working version' : ' · Historical'
                                                        ]
                                                    }, v.id))
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": Testids.rfiPackage.pin,
                                        disabled: disabled || history.isError || !versions.some((v)=>v.id === draft.versionId),
                                        onClick: ()=>void run('Pinning exact version…', async ()=>{
                                                await rfiSectionClient.selectRfiPackageVersion({
                                                    ...scope,
                                                    sectionId: draft.sectionId,
                                                    documentVersionId: draft.versionId,
                                                    requestId: requestId(`pin:${draft.packageId}:${draft.sectionId}:${draft.versionId}`)
                                                });
                                                await packages.refetch();
                                            }),
                                        children: "Pin selected version"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Externally authored final file",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            type: "file",
                            "aria-label": "Externally authored final file",
                            "data-testid": Testids.rfiPackage.file,
                            disabled: disabled || !!draft.uploaded,
                            onChange: (e)=>setFile(e.target.files?.[0])
                        })
                    }),
                    draft.uploaded && /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Upload completed: ",
                            draft.uploaded.filename,
                            ". Retry attachment to its original package; the uploaded bytes will be reused."
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiPackage.upload,
                        disabled: disabled || !file && !draft.uploaded || !user?.tenant_id || !zouProjectId,
                        onClick: ()=>void run(draft.uploaded ? 'Attaching uploaded file…' : 'Uploading external final file…', async ()=>{
                                let uploaded = current.current.uploaded;
                                if (!uploaded) {
                                    if (!file || !user?.tenant_id) return;
                                    setProgress(0);
                                    const result = await uploadAttachmentViaTesoro(file, {
                                        tenantId: user.tenant_id,
                                        ownerUserId: user.id,
                                        projectId: zouProjectId,
                                        autoProcess: false,
                                        onProgress: setProgress
                                    });
                                    uploaded = {
                                        artifactId: result.artifactId,
                                        filename: result.filename,
                                        packageId: draft.packageId
                                    };
                                    persist({
                                        uploaded
                                    });
                                }
                                await rfiSectionClient.attachRfiPackageArtifact({
                                    rfiResponseProjectId,
                                    packageId: uploaded.packageId,
                                    artifactId: uploaded.artifactId,
                                    requestId: requestId(`attach:${uploaded.packageId}:${uploaded.artifactId}`)
                                });
                                persist({
                                    uploaded: undefined
                                });
                                setFile(undefined);
                                await packages.refetch();
                            }),
                        children: draft.uploaded ? 'Retry attachment' : 'Upload and attach final file'
                    }),
                    /*#__PURE__*/ _jsxs("ul", {
                        className: "space-y-3",
                        children: [
                            selected.items.length === 0 && /*#__PURE__*/ _jsx("li", {
                                children: "No final items yet. Pin a saved version or upload a final file."
                            }),
                            selected.items.map((item)=>/*#__PURE__*/ _jsxs("li", {
                                    className: "flex flex-col items-start gap-2 rounded-md border border-border p-3",
                                    "data-testid": Testids.rfiPackage.item,
                                    "data-kind": item.kind,
                                    id: `rfi-package-item-${item.id}`,
                                    "data-item-id": item.id,
                                    "data-version-id": item.documentVersionId,
                                    "data-artifact-id": item.artifactId,
                                    children: [
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            className: "block break-all",
                                            children: [
                                                item.kind === 'response_document_version' || item.kind === 'generated_artifact' && !item.sectionId ? `${responseItemLabel(item.kind, item.exportFormat, versionNumberOf(item.documentVersionId))} ·` : null,
                                                ' ',
                                                item.kind === 'response_document_version' || item.kind === 'generated_artifact' && !item.sectionId ? null : item.kind === 'section_version' ? 'Pinned section version' : item.kind === 'generated_artifact' ? 'Generated DOCX copy' : 'Externally authored final file',
                                                ' ',
                                                "·",
                                                ' ',
                                                item.filename || sections.data?.find((s)=>s.id === item.sectionId)?.title || item.sectionId
                                            ]
                                        }),
                                        item.documentVersionId && /*#__PURE__*/ _jsxs(SpecraText, {
                                            size: "caption",
                                            className: "block break-all",
                                            children: [
                                                "Pinned document ",
                                                item.documentId,
                                                " · version ",
                                                item.documentVersionId
                                            ]
                                        }),
                                        item.artifactId && /*#__PURE__*/ _jsxs(SpecraText, {
                                            size: "caption",
                                            className: "block break-all",
                                            children: [
                                                "Artifact ",
                                                item.artifactId,
                                                " · version ",
                                                item.artifactVersion
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            size: "caption",
                                            className: "block break-all",
                                            children: [
                                                "SHA-256: ",
                                                item.contentSha256
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            className: "block",
                                            "data-testid": Testids.rfiPackage.validation,
                                            children: [
                                                "Not validated",
                                                item.validationStatus === 'unsupported' ? ' — external file validation is unsupported' : ' — no compliance approval implied'
                                            ]
                                        }),
                                        item.kind === 'section_version' || item.kind === 'response_document_version' ? /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": Testids.rfiPackage.generate,
                                            disabled: disabled || selected.items.some((other)=>other.kind === 'generated_artifact' && other.documentVersionId === item.documentVersionId && other.sectionId === item.sectionId && (other.exportFormat || 'docx') === (item.exportFormat || 'docx')),
                                            onClick: ()=>void run('Generating pinned DOCX…', async ()=>{
                                                    await rfiSectionClient.generateRfiPackageArtifact({
                                                        ...scope,
                                                        selectionItemId: item.id,
                                                        requestId: requestId(`generate:${item.id}`)
                                                    });
                                                    await packages.refetch();
                                                }),
                                            children: item.kind === 'response_document_version' ? `Generate ${(item.exportFormat || 'docx').toUpperCase()}` : 'Generate pinned DOCX'
                                        }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": Testids.rfiPackage.download,
                                            disabled: disabled,
                                            onClick: ()=>void run('Preparing download…', async ()=>{
                                                    const result = await rfiSectionClient.downloadRfiPackageArtifact({
                                                        ...scope,
                                                        itemId: item.id
                                                    });
                                                    if (!result.url) throw new Error('Download URL unavailable');
                                                    const link = document.createElement('a');
                                                    link.href = result.url;
                                                    link.download = result.filename;
                                                    link.click();
                                                }),
                                            children: "Download final file"
                                        })
                                    ]
                                }, item.id))
                        ]
                    })
                ]
            })
        ]
    });
}
